/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled } from '../../theme';

export interface SearchWidgetProps {
  search: string | null;
  focusOnMount?: boolean;
  rounded?: boolean;
  loading?: boolean;
  searchChanged?: (search: string | null) => any;
  inputRef?: React.RefObject<HTMLInputElement>;
  className?: any;
}

interface SearchWidgetState {
  value: string | null;
}

export class SearchWidget extends React.Component<SearchWidgetProps, SearchWidgetState> {
  ref: React.RefObject<HTMLInputElement>;

  constructor(props: SearchWidgetProps) {
    super(props);
    this.ref = React.createRef();
    this.state = { value: null };
  }

  componentDidMount(): void {
    if (this.props.focusOnMount) {
      this.getRef().current?.focus();
    }
  }

  fireChange(value: string | null) {
    this.setState(
      {
        value: value
      },
      () => {
        this.props.searchChanged?.(value);
      }
    );
  }

  getSearchIcon() {
    if (this.props.loading) {
      return (
        <S.Icon>
          <FontAwesomeIcon icon="sync-alt" spin={true} />
        </S.Icon>
      );
    }
    return (
      <S.Icon>
        <FontAwesomeIcon icon="search" />
      </S.Icon>
    );
  }

  getCancel() {
    if (!this.props.search) {
      return null;
    }
    return (
      <S.IconRight
        onClick={() => {
          this.fireChange(null);
          // the default value hack we have in place, means that the below line becomes necessary
          // @ts-ignore
          this.getRef().current?.value = '';
        }}
      >
        <FontAwesomeIcon icon="times" />
      </S.IconRight>
    );
  }

  getRef = (): React.RefObject<HTMLInputElement> => {
    if (this.props.inputRef) {
      return this.props.inputRef;
    }
    return this.ref;
  };

  render() {
    return (
      <S.Container rounded={this.props.rounded} className={this.props.className}>
        {this.getSearchIcon()}
        <S.Input
          ref={this.getRef()}
          defaultValue={this.props.search || ''}
          placeholder="Search"
          onChange={(event) => {
            let val: string | null = event.target.value;
            if (!val || val.trim() === '') {
              val = null;
            }

            this.fireChange(val);
          }}
        />
        {this.getCancel()}
      </S.Container>
    );
  }
}

namespace S {
  export const IconRight = styled.div`
    position: absolute;
    right: 5px;
    top: 50%;
    transform: translateY(-50%);
    color: white;
    opacity: 0.5;
    padding: 5px;
    cursor: pointer;
  `;

  export const Container = styled.div<{ rounded?: boolean }>`
    position: relative;
    background: ${(p) => p.theme.search.background};
    border-radius: 3px;
    ${(p) =>
      p.rounded &&
      css`
        border-radius: 8px;
      `}
  `;

  export const Input = styled.input`
    width: 100%;
    padding-left: 30px;
    background: transparent;
    border: none;
    outline: none;
    font-size: 14px;
    vertical-align: middle;
    line-height: 32px;
    height: 36px;
    color: ${(p) => p.theme.search.color};

    ::placeholder {
      color: ${(p) => p.theme.search.color};
      opacity: 0.5;
      user-select: none;
    }
  `;

  export const Icon = styled.div`
    position: absolute;
    pointer-events: none;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: ${(p) => p.theme.search.color};
    opacity: 0.5;
    font-size: 13px;
  `;
}
